import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Viewfinder,
  CaptureButton,
  FlipCameraButton,
  GalleryButton,
  AngleStrip,
  AngleProgress,
  LuminosityAlert,
  Chip,
  BottomSheet,
} from '../../components';
import { useInspectionContext } from '../../hooks';
import { REQUIRED_ANGLES, type Angle } from '../../types/api';
import { compressImage } from '../../utils';
import { getAngleLabel, getAngleIcon, getAngleInstructions } from '../../api';

interface AngleData {
  id: Angle;
  label: string;
  icon: string;
  instructions: string;
}

const ANGLES_DATA: AngleData[] = REQUIRED_ANGLES.map((angle) => ({
  id: angle,
  label: getAngleLabel(angle),
  icon: getAngleIcon(angle),
  instructions: getAngleInstructions(angle),
}));

export const GuidedCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    capturePhoto,
    setCurrentAngle,
    nextAngle,
    reset,
  } = useInspectionContext();

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const facingModeRef = useRef<'user' | 'environment'>('environment');
  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [luminosityLevel, setLuminosityLevel] = useState<'low' | 'high' | 'optimal'>('optimal');
  const [showAngleInfo, setShowAngleInfo] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const currentAngle = ANGLES_DATA[state.currentAngleIndex];
  const completedCount = Object.keys(state.photos).length;

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingModeRef.current,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setPermissionState('granted');
    } catch (error) {
      console.error('Camera error:', error);
      setPermissionState('denied');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    initCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [initCamera]);

  // Analyze luminosity (simple version)
  useEffect(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const checkLuminosity = () => {
      if (!videoRef.current || videoRef.current.readyState < 2) return;
      
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      ctx.drawImage(videoRef.current, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let totalBrightness = 0;
      
      // Sample every 10th pixel for performance
      for (let i = 0; i < pixels.length; i += 40) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        totalBrightness += (r + g + b) / 3;
      }
      
      const avgBrightness = totalBrightness / (pixels.length / 40);
      
      if (avgBrightness < 60) setLuminosityLevel('low');
      else if (avgBrightness > 200) setLuminosityLevel('high');
      else setLuminosityLevel('optimal');
    };

    const interval = setInterval(checkLuminosity, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCapture = async () => {
    if (!videoRef.current || !currentAngle) return;
    
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      
      // Compress
      const file = new File([blob], `${currentAngle.id}.jpg`, { type: 'image/jpeg' });
      const compressed = await compressImage(file, { maxWidth: 1920, maxHeight: 1080, quality: 0.85 });
      
      // Create preview
      const preview = URL.createObjectURL(compressed);
      
      capturePhoto(currentAngle.id, compressed);
      setCapturedImage(preview);
      
      // Show preview briefly then advance
      setTimeout(() => {
        setCapturedImage(null);
        if (state.currentAngleIndex < REQUIRED_ANGLES.length - 1) {
          nextAngle();
        }
      }, 800);
    }, 'image/jpeg', 0.85);
  };

  const handleFlipCamera = () => {
    facingModeRef.current = facingModeRef.current === 'environment' ? 'user' : 'environment';
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    initCamera();
  };

  const handleAngleClick = (angleId: string) => {
    const angle = angleId as Angle;
    const index = REQUIRED_ANGLES.indexOf(angle);
    if (index !== -1) {
      setCurrentAngle(index);
    }
  };

  const handleBack = () => {
    if (completedCount > 0) {
      // Show confirmation dialog
      setShowAngleInfo(true); // reuse for confirmation
    } else {
      navigate('/inspecao/placa');
    }
  };

  const confirmExit = () => {
    reset();
    navigate('/inspecao/placa');
  };

  const angleItems = ANGLES_DATA.map((angle) => ({
    ...angle,
    status: state.photos[angle.id] ? 'completed' as const : 
            angle.id === currentAngle?.id ? 'active' as const : 'pending' as const,
    thumbnail: state.photos[angle.id]?.preview || null,
  }));

  return (
    <Page
      appBar={{
        title: 'Captura Guiada',
        subtitle: `${state.plate}`,
        leading: <span className="material-symbols-outlined">close</span>,
        leadingAction: handleBack,
        elevation: false,
        transparent: true,
      }}
      className="h-screen pb-0"
    >
      {/* Luminosity Alert */}
      {luminosityLevel !== 'optimal' && (
        <LuminosityAlert
          level={luminosityLevel}
          className="m-4 mx-auto max-w-[428px]"
        />
      )}

      {/* Progress & Angle Strip */}
      <div className="px-4">
        <AngleProgress
          completed={completedCount}
          total={REQUIRED_ANGLES.length}
          currentAngle={currentAngle ? { id: currentAngle.id, label: currentAngle.label, status: 'active' as const } : undefined}
        />
        <AngleStrip
          angles={angleItems}
          activeAngleId={currentAngle?.id}
          onAngleClick={handleAngleClick}
        />
      </div>

      {/* Viewfinder */}
      <div className="relative flex-1 max-w-[428px] mx-auto">
        <Viewfinder
          stream={streamRef.current}
          facingMode={facingModeRef.current}
          corners={true}
          horizon={true}
          overlay={
            <>
              {/* Current angle indicator */}
              <div className="absolute top-4 left-4 right-4 flex justify-center">
                <Card variant="elevated" padding="sm" className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">{currentAngle?.icon}</span>
                    <span className="font-headline-sm text-primary">{currentAngle?.label}</span>
                  </div>
                </Card>
              </div>

              {/* Instructions */}
              <div className="absolute bottom-24 left-4 right-4">
                <Card variant="elevated" padding="md" className="glass-strong">
                  <p className="font-body-sm text-center text-on-surface">
                    {currentAngle?.instructions}
                  </p>
                </Card>
              </div>
            </>
          }
        />
        
        {/* Hidden canvas for luminosity analysis */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Captured preview overlay */}
        {capturedImage && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20 animate-in fade-in">
            <img src={capturedImage} alt="Capturada" className="max-w-[90%] max-h-[80%] rounded-xl shadow-elevation-3" />
          </div>
        )}

        {/* Camera Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
          <GalleryButton
            onOpen={() => setShowGallery(true)}
            thumbnail={state.photos[REQUIRED_ANGLES[REQUIRED_ANGLES.length - 1]]?.preview || null}
            disabled={permissionState !== 'granted'}
          />
          
          <CaptureButton
            onCapture={handleCapture}
            disabled={permissionState !== 'granted' || !!capturedImage}
            isCapturing={!!capturedImage}
            size={80}
          />
          
          <FlipCameraButton
            onFlip={handleFlipCamera}
            disabled={permissionState !== 'granted'}
          />
        </div>
      </div>

      {/* Angle Info Bottom Sheet */}
      <BottomSheet
        isOpen={showAngleInfo}
        onClose={() => setShowAngleInfo(false)}
        title={completedCount > 0 ? 'Sair da captura?' : 'Informações do ângulo'}
        subtitle={completedCount > 0 ? 'O progresso será salvo. Deseja voltar para a placa?' : undefined}
      >
        {completedCount > 0 ? (
          <div className="space-y-3">
            <Button variant="outline" fullWidth onClick={() => setShowAngleInfo(false)}>
              Continuar capturando
            </Button>
            <Button variant="secondary" fullWidth onClick={confirmExit}>
              Voltar para placa
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {ANGLES_DATA.map((angle) => (
              <Card key={angle.id} variant="interactive" padding="md" className={state.photos[angle.id] ? 'opacity-60' : ''}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-secondary-container flex items-center justify-center">
                    <span className="material-symbols-outlined text-secondary text-xl">{angle.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-label-lg">{angle.label}</h4>
                    <p className="font-body-sm text-on-surface-variant">{angle.instructions}</p>
                  </div>
                  {state.photos[angle.id] && (
                    <Chip variant="success" size="sm">Concluído</Chip>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </BottomSheet>

      {/* Gallery Bottom Sheet */}
      <BottomSheet
        isOpen={showGallery}
        onClose={() => setShowGallery(false)}
        title="Galeria"
        subtitle="Selecione uma foto para recapturar"
      >
        <div className="grid grid-cols-4 gap-2">
          {ANGLES_DATA.map((angle) => {
            const photo = state.photos[angle.id];
            return (
              <button
                key={angle.id}
                type="button"
                className="aspect-square rounded-xl overflow-hidden relative bg-surface-container"
                onClick={() => {
                  if (photo) {
                    // Show full screen preview with option to retake
                  }
                }}
              >
                {photo ? (
                  <>
                    <img src={photo.preview} alt={angle.label} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-2xl">camera_alt</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-on-surface-variant">
                    <span className="material-symbols-outlined text-3xl">{angle.icon}</span>
                    <span className="font-label-sm text-center px-2">{angle.label}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>

      {/* Next Step Button - Fixed at bottom */}
      {completedCount === REQUIRED_ANGLES.length && (
        <div className="sticky-dock max-w-[428px] mx-auto">
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/inspecao/video')}
            leftIcon={<span className="material-symbols-outlined">videocam</span>}
          >
            Continuar para o vídeo do motor
          </Button>
        </div>
      )}

      {/* Camera permission denied */}
      {permissionState === 'denied' && (
        <div className="absolute inset-0 flex items-center justify-center p-4 bg-inverse-surface z-10">
          <Card variant="elevated" padding="xl" className="text-center max-w-md">
            <span className="material-symbols-outlined text-error text-5xl mb-4 block">videocam_off</span>
            <h3 className="font-headline-md">Acesso à câmera negado</h3>
            <p className="font-body-md text-on-surface-variant mt-2 mb-4">
              Permita o acesso à câmera nas configurações do navegador para continuar.
            </p>
            <Button variant="primary" onClick={initCamera}>
              Tentar novamente
            </Button>
          </Card>
        </div>
      )}
    </Page>
  );
};